import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { X, Calendar } from 'lucide-react';
import { logger } from '@/utils/productionLogger';
import { DatingPreferences } from '@/types/profile';
import { resolveLookingForDate, mergeLookingForDate } from '@/utils/datingVisibility';

interface EditProfileModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  profile?: any;
  onSave?: (profile: any) => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen = false,
  onClose = () => {},
  profile,
  onSave
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    calculatedAge: null as number | null,
    // BlissNet: binary Discover participation toggle (replaces 3-option intent)
    lookingForDate: true,
    // V164.1: Dating preferences fields
    gender: '',
    genderPreferences: [] as string[],
    ageRangeMin: 18,
    ageRangeMax: 65
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Calculate age from date of birth
  const calculateAge = (dob: string): number | null => {
    if (!dob) return null;
    
    try {
      const birthDate = new Date(dob);
      
      if (isNaN(birthDate.getTime())) {
        return null;
      }
      
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return Math.max(18, Math.min(100, age));
    } catch (error) {
      return null;
    }
  };

  // V164.1: Calculate default age range based on user's age
  const calculateDefaultAgeRange = (userAge: number): [number, number] => {
    const minAge = Math.max(userAge - 3, 18);
    const maxAge = Math.min(userAge + 3, 100);
    return [minAge, maxAge];
  };

  // V164.1: Toggle gender preference selection
  const toggleGenderPreference = (preference: string) => {
    setFormData(prev => {
      if (preference === 'everyone') {
        // If selecting "everyone", clear other selections
        return { ...prev, genderPreferences: ['everyone'] };
      }
      
      const isSelected = prev.genderPreferences.includes(preference);
      let newPreferences;
      
      if (isSelected) {
        newPreferences = prev.genderPreferences.filter(p => p !== preference);
      } else {
        // Remove "everyone" if selecting specific gender
        newPreferences = prev.genderPreferences
          .filter(p => p !== 'everyone')
          .concat(preference);
      }
      
      return { ...prev, genderPreferences: newPreferences };
    });
  };

  // BlissNet: dating preference fields are available when Discover participation is ON
  const shouldShowDatingPreferences = () => {
    return formData.lookingForDate === true;
  };

  // Load profile data when modal opens
  useEffect(() => {
    if (isOpen && profile) {
      // Convert DOB to proper format for date input
      // V167.MERGE: Use date_of_birth instead of dob
      let dobValue = '';
      if (profile.date_of_birth) {
        const dobDate = typeof profile.date_of_birth === 'string' ? new Date(profile.date_of_birth) : profile.date_of_birth;
        if (!isNaN(dobDate.getTime())) {
          dobValue = dobDate.toISOString().split('T')[0];
        }
      }

      // V164.1: Calculate default age range if user has DOB
      let ageRangeMin = 18;
      let ageRangeMax = 65;
      if (dobValue) {
        const userAge = calculateAge(dobValue);
        if (userAge) {
          const [minAge, maxAge] = calculateDefaultAgeRange(userAge);
          ageRangeMin = minAge;
          ageRangeMax = maxAge;
        }
      }

      // V164.1: Load dating preferences if they exist
      const datingPrefs = profile.dating_preferences as DatingPreferences | undefined;
      const existingMinAge = datingPrefs?.age_preference_min;
      const existingMaxAge = datingPrefs?.age_preference_max;

      setFormData({
        name: profile.name || '',
        dateOfBirth: dobValue,
        calculatedAge: dobValue ? calculateAge(dobValue) : null,
        // BlissNet: hydrate canonical looking_for_date (missing/null -> false, opt-in)
        lookingForDate: resolveLookingForDate(datingPrefs),
        gender: profile.gender || '',
        genderPreferences: datingPrefs?.gender_preference || [],
        ageRangeMin: existingMinAge || ageRangeMin,
        ageRangeMax: existingMaxAge || ageRangeMax
      });

      logger.component('EditProfile', 'Modal opened', { 
        profileId: profile.id
      });
    }
  }, [isOpen, profile]);

  // Update calculated age when DOB changes
  useEffect(() => {
    const newAge = calculateAge(formData.dateOfBirth);
    setFormData(prev => ({ ...prev, calculatedAge: newAge }));
  }, [formData.dateOfBirth]);

  // V164.1: Initialize default age range when user's DOB changes and age range hasn't been customized
  useEffect(() => {
    if (formData.dateOfBirth && formData.calculatedAge && 
        formData.ageRangeMin === 18 && formData.ageRangeMax === 65) {
      const [minAge, maxAge] = calculateDefaultAgeRange(formData.calculatedAge);
      setFormData(prev => ({
        ...prev,
        ageRangeMin: minAge,
        ageRangeMax: maxAge
      }));
    }
  }, [formData.dateOfBirth, formData.calculatedAge]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('profile.errNameReq');
    }

    if (formData.dateOfBirth) {
      const age = calculateAge(formData.dateOfBirth);
      if (!age || age < 18) {
        newErrors.dateOfBirth = t('profile.errAgeLimit');
      } else if (age > 100) {
        newErrors.dateOfBirth = t('profile.errDobInvalid');
      }
    }

    // V164.1: Dating preferences validation (only if dating/both selected)
    if (shouldShowDatingPreferences()) {
      if (!formData.gender || formData.gender === '') {
        newErrors.gender = t('profile.errGenderReq');
      }
      if (formData.genderPreferences.length === 0) {
        newErrors.genderPreferences = t('profile.errGenderPrefReq');
      }
      if (formData.ageRangeMin >= formData.ageRangeMax) {
        newErrors.ageRange = t('profile.errAgeRange');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // BlissNet: merge canonical looking_for_date into existing dating_preferences
      // (preserve unrelated keys). When ON, also persist gender/age match preferences.
      const datingPreferences = mergeLookingForDate(
        profile?.dating_preferences,
        formData.lookingForDate
      );
      if (formData.lookingForDate) {
        datingPreferences.gender_preference = formData.genderPreferences;
        datingPreferences.age_preference_min = formData.ageRangeMin;
        datingPreferences.age_preference_max = formData.ageRangeMax;
      }

      // V167.MERGE: Use date_of_birth instead of dob
      const updatedProfile = {
        name: formData.name,
        date_of_birth: formData.dateOfBirth || null,
        // Preserve existing gender (do not wipe when participation is OFF)
        gender: formData.gender || null,
        dating_preferences: datingPreferences
      };

      logger.info('BlissNet: Profile updated with dating visibility', {
        lookingForDate: formData.lookingForDate,
        gender: formData.gender
      });

      await onSave?.(updatedProfile);

      // Telemetry: only after a successful save (fail-safe, non-blocking)
      import('@/analytics')
        .then(({ trackEvent }) =>
          trackEvent('profile_dating_visibility_updated', {
            looking_for_date: formData.lookingForDate
          })
        )
        .catch(() => {});

      onClose();
    } catch (error: unknown) {
      console.error('❌ EditProfileModal: Profile save failed:', error);
      logger.error('Profile save failed', error);
      // Error toast + fail closed: parent handleSave shows toast and rethrows
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-8">
      <div 
        className="bg-background rounded-2xl w-full max-w-md flex flex-col border shadow-2xl"
        style={{ maxHeight: 'calc(100vh - 100px)' }} // URGENT: More space for proper button positioning
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold">{t('profile.editProfile')}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-2 hover:bg-accent rounded-full"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
          <div>
            <Label htmlFor="name">{t('profile.fullName')}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('profile.placeholderName')}
              className={errors.name ? 'border-destructive' : ''}
              disabled={isSaving}
            />
            {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
          </div>

          <div>
            <Label htmlFor="dob" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t('profile.dateOfBirth')}
              {!formData.dateOfBirth && <span className="text-xs text-muted-foreground">{t('profile.optional')}</span>}
            </Label>
            <Input
              id="dob"
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
              min={new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]}
              className={errors.dateOfBirth ? 'border-destructive' : ''}
              disabled={isSaving}
            />
            {formData.dateOfBirth && formData.calculatedAge ? (
              <p className="text-sm text-muted-foreground mt-1">
                {t('profile.age', { age: formData.calculatedAge })}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                {t('profile.dobHint')}
              </p>
            )}
            {errors.dateOfBirth && <p className="text-sm text-destructive mt-1">{errors.dateOfBirth}</p>}
          </div>
        </div>

        {/* BlissNet: Discover participation toggle (replaces 3-option intent selector) */}
        <div className="mb-6 space-y-3">
          <Label className="block text-sm font-medium text-foreground">
            {t('profile.datingSectionLabel')}
          </Label>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-secondary">
            <div className="flex-1 space-y-1">
              <div className="font-medium text-foreground">
                {t('profile.lookingForDateLabel')}
              </div>
              <p className="text-sm text-muted-foreground">
                {formData.lookingForDate
                  ? t('profile.lookingForDateHelperOn')
                  : t('profile.lookingForDateHelperOff')}
              </p>
            </div>
            <Switch
              checked={formData.lookingForDate}
              onCheckedChange={(checked) => handleInputChange('lookingForDate', checked)}
              disabled={isSaving}
            />
          </div>
        </div>

        {/* V164.1: Dating Preferences Section - Only show when Dating or Both is selected */}
        {shouldShowDatingPreferences() && (
          <div className="space-y-6 border-t pt-6 mb-6">
            <h3 className="text-lg font-semibold">{t('profile.datingPreferences')}</h3>

            {/* Gender Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">{t('profile.yourGender')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'male', label: t('profile.male') },
                  { value: 'female', label: t('profile.female') },
                  { value: 'other', label: t('profile.nonBinary') },
                  { value: 'prefer-not-to-say', label: t('profile.preferNotToSay') }
                ].map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant={formData.gender === option.value ? 'default' : 'outline'}
                    onClick={() => handleInputChange('gender', option.value)}
                    className="text-sm"
                    disabled={isSaving}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              {errors.gender && <p className="text-sm text-destructive mt-1">{errors.gender}</p>}
            </div>

            {/* Gender Preferences */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">{t('profile.interestedIn')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'male', label: t('profile.men') },
                  { value: 'female', label: t('profile.women') },
                  { value: 'other', label: t('profile.nonBinary') },
                  { value: 'everyone', label: t('profile.everyone') }
                ].map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant={formData.genderPreferences.includes(option.value) ? 'default' : 'outline'}
                    onClick={() => toggleGenderPreference(option.value)}
                    className="text-sm"
                    disabled={isSaving}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              {errors.genderPreferences && <p className="text-sm text-destructive mt-1">{errors.genderPreferences}</p>}
            </div>

            {/* Age Range Slider */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">
                {t('profile.ageRange', { min: formData.ageRangeMin, max: formData.ageRangeMax })}
              </Label>
              <div dir="ltr" className="space-y-3">
                <Slider
                  dir="ltr"
                  value={[formData.ageRangeMin, formData.ageRangeMax]}
                  onValueChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      ageRangeMin: value[0],
                      ageRangeMax: value[1]
                    }));
                  }}
                  min={18}
                  max={100}
                  step={1}
                  className="w-full"
                  disabled={isSaving}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>18</span>
                  <span>100</span>
                </div>
              </div>
              {errors.ageRange && <p className="text-sm text-destructive mt-1">{errors.ageRange}</p>}
            </div>
          </div>
        )}

        </div>

        {/* FIX: Buttons positioned just above bottom navigation */}
        <div 
          className="border-t border-border bg-background/95 backdrop-blur sticky bottom-0"
          style={{ 
            marginBottom: '20px' // FIX: Closer to bottom navigation as requested
          }}
        >
          <div className="p-4 flex gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-4 py-3 bg-secondary hover:bg-accent rounded-xl font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-blue-500 hover:from-pink-600 hover:to-blue-600 text-white rounded-xl font-medium"
            >
              {isSaving ? t('common.loading') : t('common.saveChanges')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
